import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { CustomerClientDto } from '../dtos/customer.dto';
import { HttpClient } from '@angular/common/http';
import { ResponseDto } from '../dtos/response.dto';
import { ShipmentAddressDto } from '../dtos/shipment-address.dto';
import { map, tap } from 'rxjs/operators';
import { CreateOrUpdateOrderItemDto, OrderItemDto } from '../dtos/order-item.dto';
import { environment } from '../../environments/environment';
import { OrderDto } from '../dtos/order.dto';
import { OrderPricesDto } from '../dtos/order-prices.dto';
import { CalculatePricesDto } from '../dtos/calculate-prices.dto';
import { vibrate } from '../helpers/vibrate.function';
import { DeviceService } from './device-detector/device.service';
import { onWindowLoad } from '../helpers/on-window-load.function';
import { WishlistItemDto } from '../dtos/wishlist-item.dto';
import { PromoCodeService } from '../pages/checkout-page/components/promo-code/promo-code.service';
import { UpdateFullNameClientDto } from '../dtos/update-full-name-client.dto';
import { UpdatePhoneNumberRequestClientDto } from '../dtos/update-phone-number-request.client.dto';
import { OtpRequestClientDto } from '../dtos/otp-request.dto';
import { OtpConfirmDto } from '../dtos/otp-confirm.dto';
import { UpdateEmailClientDto } from '../dtos/update-email.client.dto';
import { UpdatePhoneNumberResponseClientDto } from '../dtos/update-phone-number-response.client.dto';
import { OrderService } from '../pages/checkout-page/order.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private http = inject(HttpClient);
  private deviceService = inject(DeviceService);
  private promoCodeService = inject(PromoCodeService);
  private orderService = inject(OrderService);
 // todo split to CartService

  customer2 = signal<CustomerClientDto>(null);

  private cartLocalStorageKey = 'cart-v2';
  private _customer = new BehaviorSubject<CustomerClientDto | null>(null);
  private _cart: OrderItemDto[] = [];
  private _showCartModal$ = new Subject<boolean>();
  customer$ = this._customer.asObservable();
  showCartModal$ = this._showCartModal$.asObservable();
  cartInit$ = new BehaviorSubject(false);
  prices: OrderPricesDto = new OrderPricesDto();

  get customer() { return this._customer.getValue(); }
  get cart() { return this._cart; }
  get isLoggedIn(): boolean { return !!this.customer; }
  get hasName(): boolean { return this.customer ? !!this.customer.contactInfo.firstName || !!this.customer.contactInfo.lastName : false; }
  get customerName(): string { return this.customer ? `${this.customer.contactInfo.firstName} ${this.customer.contactInfo.lastName}` : ''; }
  get customerEmail(): string { return this.customer ? this.customer.contactInfo.email : ''; }
  get promoCode() { return this.promoCodeService.getPromoCode; }

  constructor() {
    this.promoCodeService.promoCodeUpdated$
      .subscribe(() => this.updatePrices());

    this.orderService.orderPlaced$
      .subscribe(() => this.resetCart());
  }

  isLoggedIn2(): boolean {
    return !!this.customer2();
  }

  setCustomer(customer: CustomerClientDto) {
    this.customer2.set(customer);
    this._customer.next(customer);
    this.initCart();
  }

  updateFullName(dto: UpdateFullNameClientDto) {
    return this.http.put<ResponseDto<CustomerClientDto>>(`${environment.apiHost}/api/v1/customer/full-name`, dto)
      .pipe(
        tap(response => this.setCustomer(response.data)),
      );
  }

  updatePhoneNumberInit(dto: UpdatePhoneNumberRequestClientDto) {
    const url = `${environment.apiHost}/api/v1/customer/phone-number/init`;
    return this.http.post<ResponseDto<UpdatePhoneNumberResponseClientDto>>(url, dto)
      .pipe(
        tap(response => {
          if (response.data.customer) {
            this.setCustomer(response.data.customer);
          }
        }),
      );
  }

  updatePhoneNumberConfirm(dto: OtpConfirmDto) {
    return this.http.post<ResponseDto<CustomerClientDto>>(`${environment.apiHost}/api/v1/customer/phone-number/confirm`, dto)
      .pipe(
        tap(response => this.setCustomer(response.data)),
      );
  }

  updateEmailInit(dto: UpdateEmailClientDto) {
    return this.http.post<ResponseDto<OtpRequestClientDto>>(`${environment.apiHost}/api/v1/customer/email/init`, dto)
  }

  updateEmailConfirm(dto: OtpConfirmDto) {
    return this.http.post<ResponseDto<CustomerClientDto>>(`${environment.apiHost}/api/v1/customer/email/confirm`, dto)
      .pipe(
        tap(response => this.setCustomer(response.data)),
      );
  }

  confirmPhoneNumberInit(): Observable<ResponseDto<OtpRequestClientDto>> {
    const url = `${environment.apiHost}/api/v1/customer/confirm-phone-number/init`;
    return this.http.post<ResponseDto<OtpRequestClientDto>>(url, {});
  }

  confirmPhoneNumberConfirm(dto: OtpConfirmDto): Observable<ResponseDto<CustomerClientDto>> {
    const url = `${environment.apiHost}/api/v1/customer/confirm-phone-number/confirm`;
    return this.http.post<ResponseDto<CustomerClientDto>>(url, dto)
      .pipe(
        tap(response => this.setCustomer(response.data)),
      );
  }

  confirmEmailInit(): Observable<ResponseDto<OtpRequestClientDto>> {
    const url = `${environment.apiHost}/api/v1/customer/confirm-email/init`;
    return this.http.post<ResponseDto<OtpRequestClientDto>>(url, {});
  }

  confirmEmailConfirm(dto: OtpConfirmDto): Observable<ResponseDto<CustomerClientDto>> {
    const url = `${environment.apiHost}/api/v1/customer/confirm-email/confirm`;
    return this.http.post<ResponseDto<CustomerClientDto>>(url, dto)
      .pipe(
        tap(response => this.setCustomer(response.data)),
      );
  }








  fetchCustomerDetails() {
    return this.http.get<ResponseDto<CustomerClientDto>>(`${environment.apiHost}/api/v1/customer/details`);
  }

  showCartModal() {
    this._showCartModal$.next(true);
  }

  private initCart() {
    if (this.cartInit$.getValue() || !this.deviceService.isPlatformBrowser()) { return; }

    const savedCart = this.customer ? this.customer.cart : JSON.parse(localStorage.getItem(this.cartLocalStorageKey));
    this._cart = savedCart || [];
    this.cartInit$.next(true);
    onWindowLoad(() => this.updatePrices());
  }

  addToCart(sku: string, qty: number, additionalServiceIds: number[] = []) {
    const alreadyAdded = this.cart.find(item => item.sku === sku);
    if (alreadyAdded) { qty += alreadyAdded.qty; }

    const dto: CreateOrUpdateOrderItemDto = { sku, qty, additionalServiceIds, orderItems: this._cart };
    return this.http.put<ResponseDto<OrderItemDto>>(`${environment.apiHost}/api/v1/cart`, dto)
      .pipe(
        tap(response => {
          this.saveToCart(response.data);
          vibrate();
          this._showCartModal$.next(true);
          this.updatePrices();
        })
      );
  }

  updateQtyInCart(orderItemDto: OrderItemDto, qty: number) {
    const additionalServiceIds: number[] = orderItemDto.additionalServices.map(service => service.id);
    const dto: CreateOrUpdateOrderItemDto = { sku: orderItemDto.sku, qty, additionalServiceIds, orderItems: this._cart };

    return this.http.put<ResponseDto<OrderItemDto>>(`${environment.apiHost}/api/v1/cart`, dto)
      .pipe(
        tap(response => {
          this.saveToCart(response.data);
          this.updatePrices();
        })
      );
  }

  deleteFromCart(sku: string): Observable<unknown> {
    const foundIdx = this._cart.findIndex(cartItem => cartItem.sku === sku);
    if (foundIdx === -1) {
      return of(null);
    }

    this._cart.splice(foundIdx, 1);
    vibrate();
    this.saveCartToStorage();
    this.updatePrices();

    if (this.isLoggedIn) {
      return this.http.delete<ResponseDto<boolean>>(`${environment.apiHost}/api/v1/cart/${sku}`);
    } else {
      return of(null);
    }
  }

  moveCartItemToWishlist(sku: string): void {
    const found = this._cart.find(cartItem => cartItem.sku === sku);
    if (!found) {
      return;
    }

    concat(
      this.deleteFromCart(sku),
      this.createWishlistItem(found.productId)
    ).subscribe();
  }

  private saveToCart(item: OrderItemDto) {
    const itemToSave = { ...item };

    const alreadyAddedIdx = this._cart.findIndex(cartItem => cartItem.sku === item.sku);
    if (alreadyAddedIdx === -1) {
      this._cart.unshift(itemToSave);
    } else {
      this._cart[alreadyAddedIdx] = itemToSave;
    }

    this.saveCartToStorage();
  }

  private saveCartToStorage() {
    localStorage.setItem(this.cartLocalStorageKey, JSON.stringify(this.cart));
  }

  resetCart() {
    this._cart = [];
    localStorage.removeItem(this.cartLocalStorageKey);
  }

  public updatePrices() {
     const payload: CalculatePricesDto = {
        items: this.cart,
        promoCodeText : this.promoCode?.promoCodeText,
      };

    this.http.post<ResponseDto<OrderPricesDto>>(`${environment.apiHost}/api/v1/cart/prices`, payload)
      .subscribe(response => {
        this.prices = response.data
      });
  }

  fetchOrders() {
    return this.http.get<ResponseDto<OrderDto[]>>(`${environment.apiHost}/api/v1/customer/orders`);
  }

  createWishlistItem(productId: number) {
    const wishlistItem: WishlistItemDto = new WishlistItemDto(productId);
    return this.http.post<ResponseDto<CustomerClientDto>>(`${environment.apiHost}/api/v1/wishlist`, wishlistItem);
  }

  deleteItemFromWishlist(productId: number ) {
   return this.http.delete<ResponseDto<boolean>>(`${environment.apiHost}/api/v1/wishlist/${productId}`);
  }

  toggleWishlistFlag(productId: number, wishlistedItem: { isInWishlist: boolean }) {
    if (wishlistedItem.isInWishlist) {
      this.deleteItemFromWishlist(productId).subscribe(
        () => wishlistedItem.isInWishlist = false,
      );
    } else {
      this.createWishlistItem(productId).subscribe(
        () => wishlistedItem.isInWishlist = true,
      );
    }
  }

  fetchLastOrderedShipmentAddress(): Observable<ShipmentAddressDto> {
    return this.http.get<ResponseDto<ShipmentAddressDto | null>>(`${environment.apiHost}/api/v1/customer/address`)
      .pipe(
        map(response => response.data),
      );
  }
}
